/**
 * Token management — storage, validation, and user extraction.
 */

const TOKEN_KEY = 'beacon_id_token';

// --- Storage ---

export const getToken = (): string | null => sessionStorage.getItem(TOKEN_KEY);

export const setToken = (idToken: string): void => sessionStorage.setItem(TOKEN_KEY, idToken);

export const clearToken = (): void => sessionStorage.removeItem(TOKEN_KEY);

// --- Validation ---

const decodePayload = (token: string): any => {
  const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
  return JSON.parse(atob(base64));
};

export const isExpired = (token: string): boolean => {
  try {
    const payload = decodePayload(token);
    return typeof payload.exp !== 'number' || payload.exp * 1000 < Date.now();
  } catch {
    return true;
  }
};

/** Check if a valid, non-expired token exists. */
export const hasValidToken = (): boolean => {
  const token = getToken();
  return !!token && !isExpired(token);
};

// --- User info ---

export type UserRole = 'SDE' | 'FinOps' | 'Reader';

export interface UserInfo {
  email: string;
  name: string;
  alias: string;
  groups: string[];
  role: UserRole;
}

export const getUserFromToken = (token: string): UserInfo | null => {
  try {
    const payload = decodePayload(token);
    const alias = payload['identities']?.[0]?.['userId']
      || payload['cognito:username']?.split('_').pop()
      || '';

    const ldap = payload['custom:ldap_groups'];
    let groups: string[] = [];
    if (ldap) {
      try { groups = JSON.parse(ldap); }
      catch { groups = ldap.split(',').map((g: string) => g.trim()).filter(Boolean); }
    } else {
      groups = (payload['cognito:groups'] || []).filter((g: string) => !g.includes('_federate-'));
    }

    const role: UserRole = groups.includes('SDE') ? 'SDE'
      : groups.includes('FinOps') ? 'FinOps' : 'Reader';

    return { email: payload.email || '', name: payload.name || '', alias, groups, role };
  } catch {
    return null;
  }
};
